// «Πρόγραμμα ημέρας» — the one hour-by-hour plan of a day (handoff v08 #23), inside Agenda → Day (no separate module).
// One vertical grid (configurable hours, 15/30-min slots) shows, each in its own style:
//   committed appointments (solid, teal — never moved from here; they open in their own form)
//   timed tasks (movable: their time changes), the advisor's own blocks (by type: coffee / getting to know, referral
//   meeting, calls, follow-up, admin, preparation, learning, team, travel, break, personal, other — colour + icon),
//   goal suggestions (dashed, «πρόταση» — accept turns them into a planned block, «όχι σήμερα» hides them) and free time.
// Owner 22 Oct 2026: the date being planned is chosen here (today / tomorrow / day after / any date) and shown in large
// type; «Μεταφορά σε άλλη ημερομηνία» moves the whole own plan (one undo); day templates, «copy from yesterday / last
// week», a quick-add bar (type chip + start + duration, the next free slot filled in, Enter adds) and print choices.
// Add (click a slot, «+ Block»), move (drag, or ↑ ↓), resize (drag the lower edge, or Shift + ↑ ↓), cancel, complete.
// Overlaps are listed and outlined, never resolved automatically; a block over an appointment needs a confirmation and
// the appointment stays untouched. The printed sheets read the same plannerItems (core/print.ts).
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import {
  DomainError, acceptAllPlanSuggestions, acceptPlanSuggestion, addDays, applyTemplate, cancelPlanBlock, completePlanBlock, copyPlan, copySources, dailyGoals, deleteTemplate, dismissPlanSuggestion,
  freeSlots, fromMin, getOffice, goalProgress, isPlanActivity, isSocialActivity, movePlan, movePlanBlock, moveTimedTask, nextFreeStart, planCopyPreview, planMoveCheck, planSuggestions, planTask, planTemplates,
  plannerItems, plannerSettings, resizePlanBlock, saveSetting, savePlanBlock, saveTemplateFromDay, templatePreview, toMin, workingWindow, ACTIVITY_INFO, PLANNER_KEY, PLAN_ACTIVITIES, PLAN_TYPE_CHOICES,
  type PlanActivity, type PlanItem, type PlanOpts, type PlanSuggestion, type PlanBlock, type PlanTemplate, type PlanType, type Task,
} from '@vero/domain';
import { errorKey, type Command } from '@vero/store';
import { useRuntime, type Runtime } from '../core/runtime.tsx';
import { Field, Modal, PersonPicker, Seg } from '../core/ui.tsx';
import { DateField } from '../core/date-field.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate, personPath } from '../core/router.ts';
import { printBlankDaySheet, printDayPlanner, printPlanList } from '../core/print.ts';
import { openers, TaskForm } from './forms.tsx';

type Ev = { target: { value: string } };
type IconName = string;
/** icon of each block type (the same everywhere: grid, chips, legend) */
export const TYPE_ICON: Record<PlanType, IconName> = {
  client_appointment: 'appointment', coffee: 'heart', referral_meeting: 'star', calls: 'call', followup: 'followup', admin: 'mail', prep: 'document', learning: 'presentation',
  team: 'people', travel: 'pin', break: 'sun', personal: 'household', other: 'more', focus: 'bolt', task: 'task', goal: 'spark',
};
/** the label of a type (legacy kinds of blocks made before schema 17 keep their own label) */
export const typeLabel = (t: (k: string) => string, type: PlanType) => t(`planner.type.${type}`);
const DURATIONS = [15, 30, 45, 60, 90, 120, 180] as const;

/** run a planner command; an overlap with an appointment asks first (the appointment itself is never changed) */
export async function attemptPlan(rt: Runtime, mk: (o: PlanOpts) => Command, okKey: string, okParams?: Record<string, string | number>): Promise<boolean> {
  try {
    await rt.ws.run(mk({}));
    rt.toast(rt.t(okKey, okParams), 'success', rt.ws.canUndo() ? { label: rt.t('undo.action'), run: () => { void rt.undo(); } } : undefined);
    return true;
  } catch (e) {
    if (e instanceof DomainError && e.code === 'plan.overlaps_appointment') {
      if (!window.confirm(rt.t('planner.confirm_conflict'))) return false;
      try {
        await rt.ws.run(mk({ allowConflict: true }));
        rt.toast(rt.t(okKey, okParams), 'success', rt.ws.canUndo() ? { label: rt.t('undo.action'), run: () => { void rt.undo(); } } : undefined);
        return true;
      } catch (e2) { rt.toast(rt.t(errorKey(e2)), 'error'); return false; }
    }
    rt.toast(rt.t(errorKey(e)), 'error');
    return false;
  }
}

/** side-by-side lanes for overlapping items (so a conflict is visible, not hidden underneath) */
function lanesOf(items: readonly { key: string; start: string; end: string }[]): Map<string, { lane: number; lanes: number }> {
  const out = new Map<string, { lane: number; lanes: number }>();
  const sorted = [...items].sort((a, b) => toMin(a.start) - toMin(b.start) || toMin(b.end) - toMin(a.end));
  let cluster: { key: string; end: number; lane: number }[] = [];
  let clusterEnd = -1;
  const flush = () => { const n = Math.max(1, ...cluster.map((c) => c.lane + 1)); for (const c of cluster) out.set(c.key, { lane: c.lane, lanes: n }); cluster = []; };
  for (const it of sorted) {
    const s = toMin(it.start), e = toMin(it.end);
    if (s >= clusterEnd) { flush(); clusterEnd = -1; }
    const used = new Set(cluster.filter((c) => c.end > s).map((c) => c.lane));
    let lane = 0; while (used.has(lane)) lane++;
    cluster.push({ key: it.key, end: e, lane });
    clusterEnd = Math.max(clusterEnd, e);
  }
  flush();
  return out;
}
const nowMinutes = () => { const n = new Date(); return n.getHours() * 60 + n.getMinutes(); };

/** «Σχεδιάζετε για …»: today / tomorrow / the day after / any date — the date being planned, impossible to miss */
function PlanDate({ day, onDay }: { day: string; onDay?: (d: string) => void }) {
  const { t, i18n, today } = useRuntime();
  const rel = day === today ? 'today' : day === addDays(today, 1) ? 'tomorrow' : day === addDays(today, 2) ? 'after' : null;
  return (
    <div className="plan-date" data-testid="plan-date">
      <div className="plan-date-label" data-testid="plan-day-label" data-day={day}>
        <span className="tiny muted upper">{t('planner.planning_for')}</span>
        <b>{i18n.date(day, 'long')}</b>
        {rel ? <span className={`chip ${rel === 'today' ? 'gold' : 'teal'}`}>{t(`planner.rel.${rel}`)}</span> : day < today ? <span className="chip">{t('planner.rel.past')}</span> : null}
      </div>
      {onDay && <div className="row wrap" style={{ gap: 4 }}>
        {([['today', 0], ['tomorrow', 1], ['after', 2]] as const).map(([k, n]) => (
          <button key={k} type="button" className={`btn btn-sm${rel === k ? ' btn-primary' : ''}`} aria-pressed={rel === k} onClick={() => onDay(addDays(today, n))} data-testid={`plan-date-${k}`}>{t(`planner.rel.${k}`)}</button>
        ))}
        <DateField value={day} onChange={(iso) => { if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) onDay(iso); }} label={t('planner.pick_date')} testId="plan-date-input" size="sm" />
      </div>}
    </div>
  );
}

export function DayPlanner({ day, onDay }: { day: string; onDay?: (d: string) => void }) {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const g = plannerSettings(view);
  const slot = g.slotMin;
  const from = toMin(g.from), to = toMin(g.to);
  const ppm = (slot === 15 ? 15 : 26) / slot;
  const isToday = day === today;
  const items = useMemo(() => plannerItems(view, day), [view, day]);
  const sugg = useMemo(() => planSuggestions(view, day, { now: isToday ? new Date().toISOString() : undefined, items }), [view, day, items, isToday]);
  const free = useMemo(() => freeSlots(view, day, items), [view, day, items]);
  const work = workingWindow(view, day);
  const snap = dailyGoals(view, day);
  const progress = useMemo(() => (snap ? goalProgress(view, snap) : []), [view, snap]);
  const [drag, setDrag] = useState<{ key: string; start: number; end: number } | null>(null);
  const quickRef = useRef<HTMLDivElement | null>(null);
  const all: (PlanItem | PlanSuggestion)[] = [...items, ...sugg];
  const shown = all.filter((x) => toMin(x.end) > from && toMin(x.start) < to);
  const outside = all.filter((x) => !shown.includes(x));
  const lanes = lanesOf(shown.map((x) => (drag?.key === x.key ? { key: x.key, start: fromMin(drag.start), end: fromMin(drag.end) } : x)));
  const conflicts = useMemo(() => { const seen = new Set<string>(); const out: [PlanItem, PlanItem][] = []; for (const a of items) for (const k of a.conflictWith) { const id = [a.key, k].sort().join('|'); if (seen.has(id)) continue; seen.add(id); const b = items.find((x) => x.key === k); if (b) out.push([a, b]); } return out; }, [items]);
  const height = (to - from) * ppm;
  const slots: number[] = []; for (let m = from; m < to; m += slot) slots.push(m);
  const who = (x: PlanItem) => { const p = x.personId ? view.get('persons', x.personId) : undefined; const q = !p && x.prospectId ? view.get('prospects', x.prospectId) : undefined; return p?.displayName ?? q?.name ?? ''; };
  const label = (x: PlanItem | PlanSuggestion): string => {
    if (x.source === 'suggestion') return t('planner.goal_block', { goal: t(`goals.type.${x.goalType}`), count: i18n.number((x as PlanSuggestion).count) });
    if (x.source === 'appointment' || x.source === 'meeting') return [who(x), i18n.maybe(x.title)].filter(Boolean).filter((s, i, a) => i === 0 || !a[0]!.includes(s)).join(' · ') || t('planner.source.appointment');
    const p = who(x);
    return [i18n.maybe(x.title), p && !x.title.includes(p) ? p : ''].filter(Boolean).join(' · ');
  };
  const sugTitle = (s: PlanSuggestion) => t('planner.goal_block', { goal: t(`goals.type.${s.goalType}`), count: s.count });

  // --- actions ---
  const task = (x: PlanItem) => (x.taskId ? view.get('tasks', x.taskId) : undefined);
  const moveTo = (x: PlanItem, s: number, e: number, resize: boolean) => {
    if (x.source === 'block') return attemptPlan(rt, (o) => (v, c) => (resize ? resizePlanBlock(v, x.blockId!, fromMin(e), c, o) : movePlanBlock(v, x.blockId!, { start: fromMin(s) }, c, o)), 'planner.moved');
    if (x.source === 'task') return attemptPlan(rt, (o) => (v, c) => moveTimedTask(v, x.taskId!, { start: fromMin(s), minutes: e - s }, c, o), 'planner.moved');
    return Promise.resolve(false);
  };
  const open = (x: PlanItem | PlanSuggestion) => {
    if (x.source === 'suggestion') return void accept(x as PlanSuggestion);
    if (x.source === 'block') return rt.open(<BlockForm day={day} block={view.get('plan_blocks', x.blockId!)} />);
    const tk = task(x);
    if (tk && tk.status === 'open') return openers(rt).complete(tk);
    if (x.personId) navigate(personPath(x.personId));
  };
  const complete = (x: PlanItem) => {
    if (x.source === 'block') return void rt.run((v, c) => completePlanBlock(v, x.blockId!, c), isSocialActivity(x.activity) && x.personId ? 'planner.done_social' : 'planner.done');
    const tk = task(x); if (tk && tk.status === 'open') openers(rt).complete(tk);
  };
  const cancel = (x: PlanItem) => { if (x.source === 'block') void rt.run((v, c) => cancelPlanBlock(v, x.blockId!, c), 'planner.cancelled'); };
  const accept = (s: PlanSuggestion) => void rt.run((v, c) => acceptPlanSuggestion(v, day, s, sugTitle(s), c), 'planner.accepted');
  const acceptAll = () => void rt.run((v, c) => acceptAllPlanSuggestions(v, day, sugg, sugTitle, c), 'planner.accepted_all');
  const dismiss = (s: PlanSuggestion) => void rt.run((v, c) => dismissPlanSuggestion(v, day, s.key, c), 'planner.dismissed');

  const onPointerDown = (e: PointerEvent, x: PlanItem, resize: boolean) => {
    if (!x.movable || e.button !== 0) return;
    if (!resize && (e.target as HTMLElement).closest('button,a,input')) return;
    e.preventDefault();
    const y0 = e.clientY, s0 = toMin(x.start), e0 = toMin(x.end), len = e0 - s0;
    let cur: { start: number; end: number } | null = null;
    const move = (ev: PointerEvent) => {
      const dm = Math.round((ev.clientY - y0) / ppm / slot) * slot;
      cur = resize ? { start: s0, end: Math.max(s0 + slot, Math.min(to, e0 + dm)) } : (() => { const s = Math.max(from, Math.min(to - len, s0 + dm)); return { start: s, end: s + len }; })();
      setDrag({ key: x.key, ...cur });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      setDrag(null);
      if (cur && (cur.start !== s0 || cur.end !== e0)) void moveTo(x, cur.start, cur.end, resize);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
  };
  const onKey = (e: KeyboardEvent, x: PlanItem | PlanSuggestion) => {
    if (x.source === 'suggestion') {
      if (e.key === 'Enter') { e.preventDefault(); accept(x as PlanSuggestion); } else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); dismiss(x as PlanSuggestion); }
      return;
    }
    const it = x as PlanItem;
    const s = toMin(it.start), en = toMin(it.end);
    if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && it.movable) {
      e.preventDefault();
      const d = e.key === 'ArrowUp' ? -slot : slot;
      if (e.shiftKey) { if (en + d > s) void moveTo(it, s, en + d, true); } else void moveTo(it, s + d, en + d, false);
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); complete(it); }
    else if (e.key === 'Enter') { e.preventDefault(); open(it); }
    else if ((e.key === 'Delete' || e.key === 'Backspace') && it.source === 'block') { e.preventDefault(); cancel(it); }
  };
  // «n» anywhere in the planner (not while typing) jumps to the quick-add bar
  const onPlannerKey = (e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    if (e.key.toLowerCase() === 'n' && !e.ctrlKey && !e.metaKey && !e.altKey && tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') { e.preventDefault(); quickRef.current?.querySelector<HTMLInputElement>('[data-testid="qa-title"]')?.focus(); }
  };

  // untimed open tasks of the day (and overdue ones, today) that are not planned yet
  const planned = new Set(view.rows('plan_blocks').filter((b) => b.day === day && b.status === 'planned' && b.taskId).map((b) => b.taskId!));
  const unplanned = view.rows('tasks').filter((x) => x.status === 'open' && !x.dueTime && x.kind !== 'appointment' && !!x.dueDate && (isToday ? x.dueDate <= day : x.dueDate === day) && !planned.has(x.id))
    .sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? '') || a.title.localeCompare(b.title)).slice(0, 15);
  const notBefore = isToday ? Math.ceil(nowMinutes() / slot) * slot : undefined;
  const firstFree = (minutes: number) => nextFreeStart(view, day, minutes, { notBefore, slot, items });
  const planIt = (x: Task) => {
    const s = firstFree(30);
    if (s == null) return rt.toast(t('planner.no_free'), 'info');
    void attemptPlan(rt, (o) => (v, c) => planTask(v, x.id, { day, start: fromMin(s), minutes: 30 }, c, o), 'planner.planned');
  };
  const nowMin = isToday ? nowMinutes() : null;
  const goalDone = (type?: string | null) => { const p = progress.find((x) => x.type === type); return p ? t('goals.progress', { done: i18n.number(p.done), target: i18n.number(p.target) }) : ''; };
  const ownCount = items.filter((x) => x.source === 'block' && x.status === 'planned').length;

  return (
    <section className="card planner" data-testid="day-planner" onKeyDown={onPlannerKey}>
      <div className="card-head">
        <h3><Icon name="agenda" />{t('planner.title')}</h3>
        <div className="row wrap" style={{ gap: 6 }}>
          <PlannerSettings />
          <button className="btn btn-sm btn-primary" onClick={() => { const s = firstFree(30) ?? (work?.start ?? from); rt.open(<BlockForm day={day} start={fromMin(s)} end={fromMin(Math.min(to, s + 60))} />); }} data-testid="plan-add"><Icon name="plus" />{t('planner.add')}</button>
        </div>
      </div>
      <PlanDate day={day} onDay={onDay} />
      <div className="planner-tools row wrap" data-testid="plan-tools">
        <button className="btn btn-sm" onClick={() => rt.open(<TemplatesModal day={day} />)} data-testid="plan-templates"><Icon name="copy" />{t('planner.templates')}</button>
        <button className="btn btn-sm" onClick={() => rt.open(<CopyModal day={day} />)} data-testid="plan-copy"><Icon name="undo" />{t('planner.copy_from')}</button>
        <button className="btn btn-sm" disabled={!ownCount} onClick={() => rt.open(<MoveModal day={day} onDay={onDay} />)} data-testid="plan-move" title={ownCount ? undefined : t('planner.move_none')}><Icon name="arrow" />{t('planner.move')}</button>
        <button className="btn btn-sm" onClick={() => rt.open(<PrintModal day={day} />)} data-testid="plan-print"><Icon name="print" />{t('planner.print')}</button>
        {sugg.length > 0 && <button className="btn btn-sm btn-ghost" onClick={acceptAll} data-testid="plan-accept-all"><Icon name="spark" />{t('planner.accept_all', { count: sugg.length })}</button>}
      </div>
      <QuickAdd day={day} items={items} slot={slot} notBefore={notBefore} boxRef={quickRef} />
      <p className="tiny muted planner-hint">{t('planner.hint')}</p>
      {conflicts.length > 0 && <div className="planner-conflicts" role="status" data-testid="plan-conflicts"><b>{t('planner.conflicts', { count: conflicts.length })}</b>{conflicts.map(([a, b]) => <span key={a.key + b.key}>{a.start}–{a.end} {label(a)} ↔ {b.start}–{b.end} {label(b)}</span>)}</div>}
      {outside.length > 0 && <p className="tiny muted planner-hint">{t('planner.outside', { count: outside.length })}: {outside.map((x) => `${x.start} ${label(x)}`).join(' · ')}</p>}
      <div className="planner-wrap">
        <div className="planner-grid" style={{ height }} data-testid="plan-grid" data-ppm={ppm} data-from={g.from} data-slot={slot}>
          {slots.map((m) => (
            <button key={m} type="button" className={`plan-slot${m % 60 === 0 ? ' hour' : ''}${work && (m < work.start || m >= work.end) ? ' off' : ''}`} style={{ top: (m - from) * ppm, height: slot * ppm }} onClick={() => rt.open(<BlockForm day={day} start={fromMin(m)} end={fromMin(Math.min(to, m + Math.max(slot, 30)))} />)} aria-label={t('planner.add_at', { time: fromMin(m) })} data-testid="plan-slot" data-time={fromMin(m)}>
              {m % 60 === 0 && <span className="plan-hour">{fromMin(m)}</span>}
            </button>
          ))}
          <div className="plan-area">
          {free.filter((f) => f.end > from && f.start < to).map((f) => (
            <div key={f.start} className="plan-free" style={{ top: (Math.max(from, f.start) - from) * ppm, height: (Math.min(to, f.end) - Math.max(from, f.start)) * ppm }} data-testid="plan-free" aria-hidden="true">
              <span>{t('planner.free')} · {fromMin(f.start)}–{fromMin(f.end)}</span>
            </div>
          ))}
          {nowMin != null && nowMin >= from && nowMin <= to && <div className="plan-now" style={{ top: (nowMin - from) * ppm }} aria-hidden="true" />}
          {shown.map((x) => {
            const d = drag?.key === x.key ? drag : null;
            const s = d ? d.start : Math.max(from, toMin(x.start)), e = d ? d.end : Math.min(to, toMin(x.end));
            const ln = lanes.get(x.key) ?? { lane: 0, lanes: 1 };
            const sug = x.source === 'suggestion';
            const it = x as PlanItem;
            const tk = !sug ? task(it) : undefined;
            // a 15-minute block in a 30-minute grid is 13 px: one small line, its real height (never over the next item)
            const tiny = (e - s) * ppm < 20;
            return (
              <div key={x.key} role="button" tabIndex={0}
                className={`plan-item src-${x.source} kind-${x.kind} t-${x.type} st-${x.status}${x.conflictWith.length ? ' conflict' : ''}${d ? ' dragging' : ''}${x.movable ? ' movable' : ''}${ln.lanes > 1 ? ' narrow' : ''}${tiny ? ' tiny' : ''}`}
                style={{ top: (s - from) * ppm, height: tiny ? Math.max(8, (e - s) * ppm - 1) : Math.max(18, (e - s) * ppm - 2), left: `calc(${(ln.lane / ln.lanes) * 100}% + 2px)`, width: `calc(${100 / ln.lanes}% - 4px)` }}
                onPointerDown={(ev: PointerEvent) => onPointerDown(ev, it, false)} onKeyDown={(ev: KeyboardEvent) => { ev.stopPropagation(); onKey(ev, x); }} onDoubleClick={() => open(x)}
                aria-label={`${fromMin(s)}–${fromMin(e)} ${label(x)} · ${typeLabel(t, x.type)}${sug ? ` (${t('planner.suggestion')})` : ''}`}
                data-testid="plan-item" data-key={x.key} data-source={x.source} data-type={x.type} data-start={fromMin(s)} data-end={fromMin(e)}>
                <div className="plan-item-head">
                  <span className="plan-time num">{fromMin(s)}–{fromMin(e)}</span>
                  <Icon name={TYPE_ICON[x.type] ?? 'task'} />
                  {x.status === 'done' && <Icon name="check" />}
                  <b className="plan-title">{label(x)}</b>
                  {sug ? <span className="chip gold plan-chip">{t('planner.suggestion')}</span>
                    : x.committed ? <span className="chip teal plan-chip" title={t('planner.committed')}>{t('planner.source.appointment')}</span>
                    : <span className="chip plan-chip">{x.source === 'task' ? i18n.enumLabel('taskKind', x.kind) : typeLabel(t, x.type)}</span>}
                <span className="plan-actions">
                  {sug ? <>
                    <button className="btn btn-xs btn-primary" onClick={() => accept(x as PlanSuggestion)} data-testid="plan-accept">{t('planner.accept')}</button>
                    <button className="btn btn-xs btn-ghost" onClick={() => dismiss(x as PlanSuggestion)} data-testid="plan-dismiss">{t('planner.dismiss')}</button>
                  </> : <>
                    {(x.source === 'block' ? x.status === 'planned' : tk?.status === 'open') && <button className="btn btn-xs" onClick={() => complete(it)} data-testid="plan-complete" aria-label={t('planner.complete')}><Icon name="check" /></button>}
                    <button className="btn btn-xs btn-ghost" onClick={() => open(x)} data-testid="plan-edit" aria-label={x.source === 'block' ? t('planner.edit') : t('planner.open')}>{x.source === 'block' ? t('planner.edit') : t('planner.open')}</button>
                    {x.source === 'block' && x.status === 'planned' && <button className="btn btn-xs btn-ghost" onClick={() => cancel(it)} data-testid="plan-cancel" aria-label={t('planner.cancel')}><Icon name="close" /></button>}
                  </>}
                </span>
                </div>
                {(e - s) * ppm >= 40 && (x.conflictWith.length > 0 || (x.goalType && !sug)) && <span className="tiny plan-sub">
                  {x.conflictWith.length > 0 && <span className="plan-conflict-note">{t('planner.overlap')}</span>}
                  {x.goalType && !sug && <span className="muted">{goalDone(x.goalType)}</span>}
                </span>}
                {x.movable && <span className="plan-resize" onPointerDown={(ev: PointerEvent) => { ev.stopPropagation(); onPointerDown(ev, it, true); }} aria-hidden="true" data-testid="plan-resize" />}
              </div>
            );
          })}
          </div>
        </div>
        <aside className="planner-side">
          <div className="section-title">{t('planner.unplanned')} · {unplanned.length}</div>
          {unplanned.length ? <div className="stack" style={{ gap: 6 }}>{unplanned.map((x) => (
            <div key={x.id} className="plan-unplanned" data-testid="plan-unplanned-row">
              <span className="small"><b>{i18n.maybe(x.title)}</b>{x.dueDate && x.dueDate < day ? <span className="chip crit" style={{ marginInlineStart: 6 }}>{i18n.date(x.dueDate, 'dayMonth')}</span> : null}</span>
              <button className="btn btn-xs" onClick={() => planIt(x)} data-testid="plan-task-btn">{t('planner.plan_task')}</button>
            </div>
          ))}</div> : <p className="tiny muted">{t('common.empty')}</p>}
          <div className="planner-legend tiny" data-testid="plan-legend">
            <span><i className="lg lg-appt" />{t('planner.source.appointment')}</span>
            <span><i className="lg lg-sug" />{t('planner.suggestion')}</span>
            <span><i className="lg lg-free" />{t('planner.free')}</span>
            {PLAN_ACTIVITIES.map((a) => <span key={a}><i className={`lg lg-type t-${a}`} />{typeLabel(t, a)}</span>)}
          </div>
        </aside>
      </div>
    </section>
  );
}

/** quick add: a type chip, the start (the next free slot of that length is filled in), the length, an optional title —
 *  Enter adds it and the start moves on to the next free slot. «Client appointment» opens the CRM appointment form. */
function QuickAdd({ day, items, slot, notBefore, boxRef }: { day: string; items: PlanItem[]; slot: number; notBefore?: number; boxRef: { current: HTMLDivElement | null } }) {
  const rt = useRuntime();
  const { t, view } = rt;
  const [type, setType] = useState<PlanType>('calls');
  const [minutes, setMinutes] = useState<number>(ACTIVITY_INFO.calls.minutes);
  const [start, setStart] = useState<string>('');
  const [title, setTitle] = useState('');
  const [personId, setPersonId] = useState<string | null>(null);
  const [touchedStart, setTouchedStart] = useState(false);
  const suggest = (m: number) => { const s = nextFreeStart(view, day, m, { notBefore, slot, items }); return s == null ? '' : fromMin(s); };
  // the start follows the next free slot until the advisor types one
  useEffect(() => { if (!touchedStart) setStart(suggest(minutes)); }, [view, day, minutes, touchedStart, items]);
  useEffect(() => { setTouchedStart(false); }, [day]);
  const pick = (ty: PlanType) => { setType(ty); setMinutes(isPlanActivity(ty) ? ACTIVITY_INFO[ty].minutes : getOffice(view).meetingMinutes); setTouchedStart(false); if (!isSocialActivity(ty) && ty !== 'client_appointment') setPersonId(null); };
  const add = async () => {
    const s = start || suggest(minutes);
    if (!s) return rt.toast(t('planner.no_free'), 'info');
    const end = fromMin(Math.min(24 * 60 - 1, toMin(s) + minutes));
    if (type === 'client_appointment') { rt.open(<TaskForm kind="appointment" personId={personId} preset={{ dueDate: day, dueTime: s, durationMin: minutes, title }} />); return; }
    if (!isPlanActivity(type)) return;
    const ok = await attemptPlan(rt, (o) => (v, c) => savePlanBlock(v, { day, start: s, end, activity: type, title: title.trim() || typeLabel(t, type), personId: isSocialActivity(type) ? personId : null }, c, o), 'planner.saved');
    if (ok) { setTitle(''); setPersonId(null); setTouchedStart(false); }
  };
  const onEnter = (e: KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); void add(); } };
  const onChipKey = (e: KeyboardEvent, i: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault(); e.stopPropagation();
    const n = PLAN_TYPE_CHOICES.length, j = (i + (e.key === 'ArrowRight' ? 1 : -1) + n) % n;
    pick(PLAN_TYPE_CHOICES[j]!);
    (e.currentTarget.parentElement?.children[j] as HTMLElement | undefined)?.focus();
  };
  return (
    <div className="plan-quick" ref={boxRef} data-testid="plan-quick">
      <div className="plan-chips" role="radiogroup" aria-label={t('planner.field.kind')}>
        {PLAN_TYPE_CHOICES.map((ty, i) => (
          <button key={ty} type="button" role="radio" aria-checked={type === ty} tabIndex={type === ty ? 0 : -1} className={`plan-type-chip t-${ty}${type === ty ? ' on' : ''}`} onClick={() => pick(ty)} onKeyDown={(e: KeyboardEvent) => onChipKey(e, i)} data-testid={`qa-type-${ty}`}>
            <Icon name={TYPE_ICON[ty]} />{typeLabel(t, ty)}
          </button>
        ))}
      </div>
      <div className="plan-quick-row">
        <input className="input input-sm" type="time" step={300} value={start} onChange={(e: Ev) => { setStart(e.target.value); setTouchedStart(true); }} onKeyDown={onEnter} aria-label={t('planner.field.start')} data-testid="qa-start" />
        <select className="input input-sm" value={String(minutes)} onChange={(e: Ev) => { setMinutes(Number(e.target.value)); }} onKeyDown={onEnter} aria-label={t('planner.duration')} data-testid="qa-duration">
          {[...new Set([...DURATIONS, minutes])].sort((a, b) => a - b).map((m) => <option key={m} value={m}>{t('planner.minutes', { n: m })}</option>)}
        </select>
        <input className="input input-sm grow" value={title} placeholder={typeLabel(t, type)} onChange={(e: Ev) => setTitle(e.target.value)} onKeyDown={onEnter} maxLength={200} aria-label={t('planner.field.title')} data-testid="qa-title" />
        {(isSocialActivity(type) || type === 'client_appointment') && <div className="plan-quick-person" data-testid="qa-person"><PersonPicker value={personId} onChange={setPersonId} /></div>}
        <button className="btn btn-sm btn-primary" onClick={() => void add()} data-testid="qa-add"><Icon name="plus" />{type === 'client_appointment' ? t('planner.qa_appointment') : t('planner.qa_add')}</button>
      </div>
      {isSocialActivity(type) && <p className="tiny muted" style={{ margin: '4px 0 0' }}>{t('planner.social_hint')}</p>}
      {type === 'client_appointment' && <p className="tiny muted" style={{ margin: '4px 0 0' }}>{t('planner.appointment_hint')}</p>}
    </div>
  );
}

function PlannerSettings() {
  const rt = useRuntime();
  const { t, view } = rt;
  const g = plannerSettings(view);
  const save = (patch: Partial<typeof g>) => void rt.run((v, c) => saveSetting(v, PLANNER_KEY, { ...g, ...patch }, c));
  const hours = Array.from({ length: 19 }, (_, i) => `${String(i + 5).padStart(2, '0')}:00`);
  return (
    <div className="row planner-settings" style={{ gap: 4 }}>
      <select className="input input-sm" value={g.from} onChange={(e: Ev) => save({ from: e.target.value })} aria-label={t('planner.hours_from')} data-testid="planner-from">{hours.filter((h) => toMin(h) <= toMin(g.to) - 120).map((h) => <option key={h}>{h}</option>)}</select>
      <select className="input input-sm" value={g.to} onChange={(e: Ev) => save({ to: e.target.value })} aria-label={t('planner.hours_to')} data-testid="planner-to">{hours.filter((h) => toMin(h) >= toMin(g.from) + 120).map((h) => <option key={h}>{h}</option>)}</select>
      <Seg values={['15', '30'] as const} value={String(g.slotMin) as '15' | '30'} onChange={(x) => save({ slotMin: Number(x) as 15 | 30 })} label={(x) => t('planner.minutes', { n: x })} testId="planner-slot" />
    </div>
  );
}

/** add or edit one of the advisor's own blocks (appointments are never edited here) */
export function BlockForm({ day, block, start, end }: { day: string; block?: PlanBlock; start?: string; end?: string }) {
  const rt = useRuntime();
  const { t } = rt;
  // the type: the block's activity (schema 17), else its legacy kind (a block made before 17 keeps it until changed)
  const initial: string = block?.activity ?? (block && block.kind !== 'focus' && isPlanActivity(block.kind) ? block.kind : block?.kind) ?? 'other';
  const [f, setF] = useState({ title: block?.title ?? '', type: initial, start: block?.start ?? start ?? '09:00', end: block?.end ?? end ?? '10:00', notes: block?.notes ?? '', personId: block?.personId ?? null as string | null });
  const legacy = !isPlanActivity(f.type) ? [f.type] : [];
  const save = async () => {
    const activity: PlanActivity | null = isPlanActivity(f.type) ? f.type : null;
    const kind = activity ? undefined : (f.type as PlanBlock['kind']);
    const fallback = activity ? typeLabel(t, activity) : t(`planner.type.${f.type}`);
    const ok = await attemptPlan(rt, (o) => (v, c) => savePlanBlock(v, { id: block?.id, day, start: f.start, end: f.end, kind, activity, title: f.title || fallback, notes: f.notes, taskId: block?.taskId ?? null, goalType: block?.goalType ?? null, source: block?.source, personId: activity && isSocialActivity(activity) ? f.personId : undefined }, c, o), 'planner.saved');
    if (ok) rt.close();
  };
  return (
    <Modal title={block ? t('planner.form_title') : t('planner.form_new')} footer={<>
      {block && block.status === 'planned' && <button className="btn btn-ghost" onClick={async () => { if (await rt.run((v, c) => cancelPlanBlock(v, block.id, c), 'planner.cancelled')) rt.close(); }} data-testid="block-cancel">{t('planner.cancel')}</button>}
      <button className="btn" onClick={() => rt.close()}>{t('common.cancel')}</button>
      <button className="btn btn-primary" onClick={() => void save()} data-testid="block-save">{t('common.save')}</button>
    </>}>
      <div className="form-grid" data-testid="block-form">
        <Field label={t('planner.field.title')} span2><input className="input" value={f.title} placeholder={t(`planner.type.${f.type}`)} onChange={(e: Ev) => setF({ ...f, title: e.target.value })} data-testid="block-title" maxLength={200} /></Field>
        <Field label={t('planner.field.kind')}><select className="input" value={f.type} onChange={(e: Ev) => setF({ ...f, type: e.target.value })} data-testid="block-kind">
          {[...legacy, ...PLAN_ACTIVITIES].map((k) => <option key={k} value={k}>{t(`planner.type.${k}`)}</option>)}
        </select></Field>
        <div />
        {isSocialActivity(f.type) && <Field label={t('planner.field.person')} span2 hint={t('planner.social_hint')}><div data-testid="block-person"><PersonPicker value={f.personId} onChange={(id) => setF({ ...f, personId: id })} /></div></Field>}
        <Field label={t('planner.field.start')}><input className="input" type="time" step={300} value={f.start} onChange={(e: Ev) => setF({ ...f, start: e.target.value })} data-testid="block-start" /></Field>
        <Field label={t('planner.field.end')}><input className="input" type="time" step={300} value={f.end} onChange={(e: Ev) => setF({ ...f, end: e.target.value })} data-testid="block-end" /></Field>
        <Field label={t('planner.field.notes')} span2><textarea className="input" rows={2} value={f.notes} onChange={(e: Ev) => setF({ ...f, notes: e.target.value })} data-testid="block-notes" /></Field>
      </div>
    </Modal>
  );
}

/** a target date: tomorrow / the day after / next week / any date */
function DateChoice({ value, onChange, base, testId }: { value: string; onChange: (d: string) => void; base: string; testId: string }) {
  const { t, i18n, today } = useRuntime();
  const opts = [...new Set([addDays(today, 0), addDays(today, 1), addDays(today, 2), addDays(base, 1), addDays(base, 7)])].filter((d) => d !== base).slice(0, 4);
  return (
    <div className="stack" style={{ gap: 6 }} data-testid={testId}>
      <div className="row wrap" style={{ gap: 4 }}>{opts.map((d) => <button key={d} type="button" className={`btn btn-sm${value === d ? ' btn-primary' : ''}`} onClick={() => onChange(d)} data-testid={`${testId}-${d}`}>{d === today ? t('planner.rel.today') : d === addDays(today, 1) ? t('planner.rel.tomorrow') : d === addDays(today, 2) ? t('planner.rel.after') : i18n.date(d, 'weekday')}</button>)}</div>
      <DateField value={value} onChange={(iso) => { if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) onChange(iso); }} label={t('planner.pick_date')} testId={`${testId}-input`} />
    </div>
  );
}

/** «Μεταφορά σε άλλη ημερομηνία»: the own plan moves; CRM appointments never (they are listed); undo in the toast */
function MoveModal({ day, onDay }: { day: string; onDay?: (d: string) => void }) {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const [to, setTo] = useState(day === today ? addDays(today, 1) : addDays(day, 1));
  const chk = useMemo(() => planMoveCheck(view, day, to), [view, day, to]);
  const go = async () => {
    const ok = await attemptPlan(rt, (o) => (v, c) => movePlan(v, day, to, c, o), 'planner.moved_day', { count: chk.blocks.length, date: i18n.date(to, 'long') });
    if (ok) { rt.close(); onDay?.(to); }
  };
  return (
    <Modal title={t('planner.move')} footer={<>
      <button className="btn" onClick={() => rt.close()}>{t('common.cancel')}</button>
      <button className="btn btn-primary" disabled={!chk.blocks.length || to === day} onClick={() => void go()} data-testid="move-confirm">{t('planner.move_go', { count: chk.blocks.length })}</button>
    </>}>
      <div className="stack" style={{ gap: 10 }} data-testid="move-modal">
        <p className="small" style={{ margin: 0 }}>{t('planner.move_intro', { date: i18n.date(day, 'long') })}</p>
        <DateChoice value={to} onChange={setTo} base={day} testId="move-date" />
        <p className="small" data-testid="move-summary" style={{ margin: 0 }}><b>{t('planner.move_what', { count: chk.blocks.length, date: i18n.date(to, 'long') })}</b></p>
        {chk.staying.length > 0 && <div className="callout warn small" data-testid="move-staying">
          <b>{t('planner.move_staying', { count: chk.staying.length })}</b>
          <ul style={{ margin: '4px 0 0' }}>{chk.staying.map((x) => <li key={x.key}>{x.start} · {i18n.maybe(x.title) || t('planner.source.appointment')}</li>)}</ul>
        </div>}
        {chk.overAppointments.length > 0 && <div className="callout crit small" data-testid="move-over">{t('planner.move_over', { count: chk.overAppointments.length })}</div>}
        {chk.overOther.length > 0 && <p className="tiny muted" style={{ margin: 0 }}>{t('planner.move_overlap', { count: chk.overOther.length })}</p>}
        <p className="tiny muted" style={{ margin: 0 }}>{t('planner.move_undo')}</p>
      </div>
    </Modal>
  );
}

/** «Αντιγραφή από χθες / από την ίδια μέρα της προηγούμενης εβδομάδας» */
function CopyModal({ day }: { day: string }) {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const src = copySources(day);
  const row = (key: 'yesterday' | 'lastWeek', from: string) => {
    const p = planCopyPreview(view, from, day);
    return (
      <div className="plan-option" key={key}>
        <div><b>{t(`planner.copy.${key}`)}</b><div className="tiny muted">{i18n.date(from, 'long')} · {t('planner.copy_counts', { add: p.add.length, skipped: p.skipped })}</div></div>
        <button className="btn btn-sm btn-primary" disabled={!p.add.length} onClick={async () => { if (await attemptPlan(rt, () => (v, c) => copyPlan(v, from, day, c), 'planner.copied', { count: p.add.length })) rt.close(); }} data-testid={`copy-${key}`}>{t('planner.copy_go')}</button>
      </div>
    );
  };
  return (
    <Modal title={t('planner.copy_from')} footer={<button className="btn" onClick={() => rt.close()}>{t('common.close')}</button>}>
      <div className="stack" style={{ gap: 8 }} data-testid="copy-modal">
        <p className="small" style={{ margin: 0 }}>{t('planner.copy_intro', { date: i18n.date(day, 'long') })}</p>
        {row('yesterday', src.yesterday)}
        {row('lastWeek', src.lastWeek)}
      </div>
    </Modal>
  );
}

/** day templates: built-in (office / appointments / calls day) and the advisor's own; save this day as a template */
function TemplatesModal({ day }: { day: string }) {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const [name, setName] = useState('');
  const lbl = (a: PlanActivity) => typeLabel(t, a);
  const tplName = (x: PlanTemplate) => (x.builtIn ? t(x.name) : x.name);
  const apply = async (x: PlanTemplate, n: number) => { if (await attemptPlan(rt, () => (v, c) => applyTemplate(v, day, x, lbl, c), 'planner.template_applied', { count: n, name: tplName(x) })) rt.close(); };
  const own = view.where('plan_blocks', 'day', day).filter((b) => b.status !== 'cancelled' && !b.taskId).length;
  return (
    <Modal title={t('planner.templates')} footer={<button className="btn" onClick={() => rt.close()}>{t('common.close')}</button>}>
      <div className="stack" style={{ gap: 8 }} data-testid="templates-modal">
        <p className="small" style={{ margin: 0 }}>{t('planner.templates_intro', { date: i18n.date(day, 'long') })}</p>
        {planTemplates(view).map((x) => {
          const p = templatePreview(view, day, x, lbl);
          return (
            <div className="plan-option" key={x.id} data-testid="tpl-row" data-id={x.id}>
              <div className="grow"><b>{tplName(x)}</b>{x.builtIn ? null : <span className="chip" style={{ marginInlineStart: 6 }}>{t('planner.template_own')}</span>}
                <div className="plan-tpl-strip" aria-hidden="true">{x.blocks.map((b, i) => <i key={i} className={`t-${b.activity}`} title={`${b.start}–${b.end} ${lbl(b.activity)}`} />)}</div>
                <div className="tiny muted">{t('planner.copy_counts', { add: p.add.length, skipped: p.skipped })}</div></div>
              <div className="row" style={{ gap: 4 }}>
                <button className="btn btn-sm btn-primary" disabled={!p.add.length} onClick={() => void apply(x, p.add.length)} data-testid={`tpl-apply-${x.id}`}>{t('planner.template_apply')}</button>
                {!x.builtIn && <button className="btn btn-sm btn-ghost" onClick={() => { if (window.confirm(t('planner.template_delete_confirm', { name: x.name }))) void rt.run((v, c) => deleteTemplate(v, x.id, c), 'planner.template_deleted'); }} data-testid="tpl-delete" aria-label={t('planner.template_delete')}><Icon name="trash" /></button>}
              </div>
            </div>
          );
        })}
        <div className="plan-option" style={{ alignItems: 'end' }}>
          <Field label={t('planner.template_save_label', { count: own })}><input className="input" value={name} onChange={(e: Ev) => setName(e.target.value)} placeholder={t('planner.template_name_ph')} maxLength={80} data-testid="tpl-name" /></Field>
          <button className="btn btn-sm" disabled={!own || !name.trim()} onClick={() => { void rt.run((v, c) => saveTemplateFromDay(v, day, name, c), 'planner.template_saved').then((ok) => { if (ok) setName(''); }); }} data-testid="tpl-save">{t('planner.template_save')}</button>
        </div>
      </div>
    </Modal>
  );
}

/** print choices: the filled plan (a list that never overlaps), a blank hourly sheet, the two-page daily sheet */
function PrintModal({ day }: { day: string }) {
  const rt = useRuntime();
  const { t, view } = rt;
  const g = plannerSettings(view);
  const [kind, setKind] = useState<'plan' | 'blank' | 'sheet'>('plan');
  const [o, setO] = useState({ from: g.from, to: g.to, goals: true, due: true, notes: true });
  const hours = Array.from({ length: 19 }, (_, i) => `${String(i + 5).padStart(2, '0')}:00`);
  const go = () => {
    if (kind === 'plan') printPlanList(rt, day, { due: o.due, notesArea: o.notes });
    else if (kind === 'blank') printBlankDaySheet(rt, day, { from: o.from, to: o.to, goals: o.goals });
    else printDayPlanner(rt, day);
    rt.close();
  };
  const opt = (k: typeof kind, icon: IconName) => (
    <label className={`plan-option${kind === k ? ' on' : ''}`} key={k}>
      <input type="radio" name="plan-print" checked={kind === k} onChange={() => setKind(k)} data-testid={`print-kind-${k}`} />
      <Icon name={icon} />
      <div className="grow"><b>{t(`planner.print_kind.${k}`)}</b><div className="tiny muted">{t(`planner.print_hint.${k}`)}</div></div>
    </label>
  );
  return (
    <Modal title={t('planner.print')} footer={<>
      <button className="btn" onClick={() => rt.close()}>{t('common.cancel')}</button>
      <button className="btn btn-primary" onClick={go} data-testid="print-go"><Icon name="print" />{t('planner.print_go')}</button>
    </>}>
      <div className="stack" style={{ gap: 8 }} data-testid="print-modal">
        {opt('plan', 'agenda')}
        {kind === 'plan' && <div className="row wrap plan-print-opts">
          <label className="check"><input type="checkbox" checked={o.due} onChange={(e: { target: HTMLInputElement }) => setO({ ...o, due: e.target.checked })} />{t('planner.print_due')}</label>
          <label className="check"><input type="checkbox" checked={o.notes} onChange={(e: { target: HTMLInputElement }) => setO({ ...o, notes: e.target.checked })} />{t('planner.print_notes')}</label>
        </div>}
        {opt('blank', 'document')}
        {kind === 'blank' && <div className="row wrap plan-print-opts">
          <label className="row small" style={{ gap: 4 }}>{t('planner.field.start')}<select className="input input-sm" value={o.from} onChange={(e: Ev) => setO({ ...o, from: e.target.value })} data-testid="blank-from">{hours.filter((h) => toMin(h) <= toMin(o.to) - 120).map((h) => <option key={h}>{h}</option>)}</select></label>
          <label className="row small" style={{ gap: 4 }}>{t('planner.field.end')}<select className="input input-sm" value={o.to} onChange={(e: Ev) => setO({ ...o, to: e.target.value })} data-testid="blank-to">{hours.filter((h) => toMin(h) >= toMin(o.from) + 120).map((h) => <option key={h}>{h}</option>)}</select></label>
          <label className="check"><input type="checkbox" checked={o.goals} onChange={(e: { target: HTMLInputElement }) => setO({ ...o, goals: e.target.checked })} data-testid="blank-goals" />{t('planner.print_goals')}</label>
        </div>}
        {opt('sheet', 'print')}
      </div>
    </Modal>
  );
}
